import { useEffect, useRef, useCallback } from 'react'
import { useScreenClass } from '@vp/swan'
import {
  type AdaptedResponse,
  onProductListView,
  onProductClick
} from '@vp/product-recommendations-tracking'
import { ProductRecommendationItem } from '../../types/Recommendations'
import { getEffectiveTilesPerRow } from '../../utils/recommendations/productRecommendationsBuilder'

interface UseBestsellersTrackingParams {
  adaptedResponse: AdaptedResponse | undefined
  products: ProductRecommendationItem[]
  desktopTilesPerRow: 4 | 6
  pageSection: string
  merchandisingCategoryId: string
}

interface UseBestsellersTrackingResult {
  setRowRef: (rowIndex: number) => (el: HTMLDivElement | null) => void
  onTileClick: (product: ProductRecommendationItem, globalIndex: number) => void
}

const MAX_PRODUCTS_PER_EVENT = 12

export const useBestsellersTracking = ({
  adaptedResponse,
  products,
  desktopTilesPerRow,
  pageSection,
  merchandisingCategoryId
}: UseBestsellersTrackingParams): UseBestsellersTrackingResult => {
  const screenClass = useScreenClass()
  const effectiveTilesPerRow = getEffectiveTilesPerRow(desktopTilesPerRow, screenClass)
  const pageName = `${merchandisingCategoryId}:Category Page`

  const trackedIndices = useRef(new Set<number>())
  const initialDeliveryDone = useRef(false)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const rowIndexByElement = useRef(new Map<Element, number>())

  const adaptedResponseRef = useRef(adaptedResponse)
  const screenClassRef = useRef(screenClass)
  const pageSectionRef = useRef(pageSection)
  const pageNameRef = useRef(pageName)
  const tilesPerRowRef = useRef(effectiveTilesPerRow)
  const productsLengthRef = useRef(products.length)
  adaptedResponseRef.current = adaptedResponse
  screenClassRef.current = screenClass
  pageSectionRef.current = pageSection
  pageNameRef.current = pageName
  tilesPerRowRef.current = effectiveTilesPerRow
  productsLengthRef.current = products.length

  const emitChunked = useCallback((indices: number[]) => {
    const response = adaptedResponseRef.current
    if (!response || indices.length === 0) return
    let i = 0
    while (i < indices.length) {
      const batch = indices.slice(i, i + MAX_PRODUCTS_PER_EVENT)
      onProductListView({
        adaptedResponse: response,
        visibleProductIndices: batch,
        screenClass: screenClassRef.current,
        set: 1,
        urlId: window.location.pathname,
        recommendationsPlacement: pageSectionRef.current,
        page: { pageName: pageNameRef.current }
      })
      i += MAX_PRODUCTS_PER_EVENT
    }
  }, [])

  const takeNewIndicesForRow = useCallback((rowIndex: number): number[] => {
    const rowStart = rowIndex * tilesPerRowRef.current
    const rowEnd = Math.min(rowStart + tilesPerRowRef.current, productsLengthRef.current)
    const newIndices: number[] = []
    for (let i = rowStart; i < rowEnd; i++) {
      if (!trackedIndices.current.has(i)) {
        trackedIndices.current.add(i)
        newIndices.push(i)
      }
    }
    return newIndices
  }, [])

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect()
      observerRef.current = null
      rowIndexByElement.current.clear()
    }
  }, [])

  const setRowRef = useCallback(
    (rowIndex: number) => (el: HTMLDivElement | null) => {
      if (!el || !adaptedResponse) return

      if (!observerRef.current) {
        observerRef.current = new IntersectionObserver(
          (entries) => {
            const observer = observerRef.current
            if (!observer) return

            const visible = entries
              .filter((entry) => entry.isIntersecting)
              .map((entry) => ({
                rowIndex: rowIndexByElement.current.get(entry.target),
                target: entry.target
              }))
              .filter((r): r is { rowIndex: number; target: Element } => r.rowIndex !== undefined)
              .sort((a, b) => a.rowIndex - b.rowIndex)

            if (visible.length === 0) {
              initialDeliveryDone.current = true
              return
            }

            if (!initialDeliveryDone.current) {
              initialDeliveryDone.current = true
              const batched: number[] = []
              for (const { rowIndex, target } of visible) {
                batched.push(...takeNewIndicesForRow(rowIndex))
                observer.unobserve(target)
              }
              try {
                emitChunked(batched)
              } catch (e) {
                // Swallow — tracking errors must not break the observer
              }
            } else {
              for (const { rowIndex, target } of visible) {
                const newIndices = takeNewIndicesForRow(rowIndex)
                observer.unobserve(target)
                if (newIndices.length > 0) {
                  try {
                    emitChunked(newIndices)
                  } catch (e) {
                    // Swallow — tracking errors must not break the observer
                  }
                }
              }
            }
          },
          { threshold: 0.5 }
        )
      }

      rowIndexByElement.current.set(el, rowIndex)
      observerRef.current.observe(el)
    },
    [adaptedResponse, emitChunked, takeNewIndicesForRow]
  )

  const onTileClick = useCallback(
    (product: ProductRecommendationItem, globalIndex: number) => {
      if (adaptedResponseRef.current) {
        const adaptedProduct = adaptedResponseRef.current.products[globalIndex]
        if (adaptedProduct) {
          onProductClick({
            adaptedResponse: adaptedResponseRef.current,
            product: adaptedProduct,
            clickedElement: 'Tile',
            urlId: window.location.pathname,
            recommendationsPlacement: pageSectionRef.current,
            page: { pageName: pageNameRef.current }
          })
        }
      }
    },
    [merchandisingCategoryId, pageSection]
  )

  return { setRowRef, onTileClick }
}
